// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';

import Menu from 'antd/lib/menu';
import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Checkbox from 'antd/lib/checkbox';
import Collapse from 'antd/lib/collapse';

// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuInfo } from 'rc-menu/lib/interface';
import CVATTooltip from 'components/common/cvat-tooltip';
import {
    getCore, Job, JobStage, User,
} from 'cvat-core-wrapper';
import { GetJobStageChinese, GetJobStateChinese } from 'cvat-core/src/enums';
import { getDeletedFrames } from 'cvat-core/src/frames';
import { hasPermissionForJobAfterInspectionStage, hasPermissionForJobAfterQualityCtlStage } from 'utils/permissions';

const core = getCore();

interface Props {
    taskMode: string;
    orgInstance: any;
    jobInstance: any;
    user: User;
    onClickMenu(params: MenuInfo): void;
    stopFrame: number;
    removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly:boolean): void;
    setForceExitAnnotationFlag(forceExit: boolean): void;
    saveAnnotations(jobInstance: any, needAsync: boolean, afterSave?: () => void): void;
}

export enum Actions {
    LOAD_JOB_ANNO = 'load_job_anno',
    EXPORT_JOB_DATASET = 'export_job_dataset',
    REMOVE_ANNO = 'remove_anno',
    OPEN_TASK = 'open_task',
    AUTO_ANNOTATE_JOB = 'auto_annotate_job',
    REVERT_VALIDATION = 'revert_validation', // action -> ("stage": "annotation", "state": "rejected")
    REVERT_INSPECTION = 'revert_inspection', // action -> ("stage": "validation", "state": "rejected")
    REVERT_QUALITYCTL = 'revert_qualityctl', // action -> ("stage": "inspection", "state": "rejected")
    FINISH_ANNOTATION = 'finish_annotation', // finish annotation -> arrive at the validation stage
    FINISH_VALIDATION = 'finish_validation', // finish validation -> arrive at the inspection stage
    FINISH_INSPECTION = 'finish_inspection', // finish inspection -> arrive at the qualityctl stage
    FINISH_QUALITYCTL = 'finish_qualityctl', // finish qualityctl -> arrive at the acceptance stage and set the completed state
    FINISH_ACCEPTANCE = 'finish_acceptance', // finish acceptance -> set the completed state
    RENEW_JOB = 'renew_job',
}

function AnnotationMenuComponent(props: Props & RouteComponentProps): JSX.Element {
    const {
        jobInstance,
        user,
        stopFrame,
        history,
        onClickMenu,
        removeAnnotations,
        setForceExitAnnotationFlag,
        saveAnnotations,
    } = props;

    const jobStage = jobInstance.stage;
    const jobState = jobInstance.state;
    const taskID = jobInstance.taskId;
    const { JobState } = core.enums;

    async function onClickMenuWrapper(params: MenuInfo): Promise<void> {
        function checkUnsavedChanges(_params: MenuInfo): void {
            if (jobInstance.annotations.hasUnsavedChanges()) {
                Modal.confirm({
                    title: '当前作业还有未保存的标注,必须保存后才能切换阶段和状态',
                    content: '是否需要先保存标注再继续？',
                    className: 'cvat-modal-content-save-job',
                    okButtonProps: {
                        children: '是',
                    },
                    cancelButtonProps: {
                        children: '否',
                    },
                    onOk: () => {
                        saveAnnotations(jobInstance, false, () => onClickMenu(_params));
                    },
                    onCancel: () => {
                        // do not ask leave confirmation
                        setForceExitAnnotationFlag(true);
                        setTimeout(() => {
                            // 只有修改状态和阶段时需要检查保存，如果不保存就不允许执行下一步操作。
                            // onClickMenu(_params);
                        });
                    },
                });
            } else {
                onClickMenu(_params);
            }
        }

        async function checkCurrentUserIsAnnotator(currentUser: User, job: Job): Promise<boolean> {
            if (!job.assignee) {
                return false;
            }
            return job.assignee?.username === currentUser.username;
        }

        if (params.key === Actions.REMOVE_ANNO) {
            let removeFrom: number;
            let removeUpTo: number;
            let removeOnlyKeyframes = false;
            const { Panel } = Collapse;
            Modal.confirm({
                title: '移除标注',
                content: (
                    <div>
                        <Text>您将要移除所有在客户端侧的标注。</Text>
                        <Text>此操作并不会影响到已保存到服务器的标注。是否继续？</Text>
                        <br />
                        <br />
                        <Collapse bordered={false}>
                            <Panel header={<Text>选择范围</Text>} key={1}>
                                <Text>从 </Text>
                                <InputNumber
                                    min={0}
                                    max={stopFrame}
                                    onChange={(value) => {
                                        removeFrom = value;
                                    }}
                                />
                                <Text> 到 </Text>
                                <InputNumber
                                    min={0}
                                    max={stopFrame}
                                    onChange={(value) => { removeUpTo = value; }}
                                />
                                <CVATTooltip title='只对上述范围内的标注有效'>
                                    <br />
                                    <br />
                                    <Checkbox
                                        onChange={(check) => {
                                            removeOnlyKeyframes = check.target.checked;
                                        }}
                                    >
                                        只删除追踪（track）中的关键帧
                                    </Checkbox>
                                </CVATTooltip>
                            </Panel>
                        </Collapse>
                    </div>
                ),
                className: 'cvat-modal-confirm-remove-annotation',
                onOk: () => {
                    removeAnnotations(removeFrom, removeUpTo, removeOnlyKeyframes);
                },
                okButtonProps: {
                    type: 'primary',
                    danger: true,
                },
                okText: '删除',
                cancelText: '取消',
            });
        } else if (params.key.startsWith('state:')) {
            Modal.confirm({
                title: '您是否想改变当前作业的状态？',
                content: `作业状态将被切换到： ${GetJobStateChinese(`${params.key.split(':')[1]}`)}。是否继续？`,
                okText: '继续',
                cancelText: '取消',
                className: 'cvat-modal-content-change-job-state',
                onOk: () => {
                    checkUnsavedChanges(params);
                },
            });
        } else if (params.key === Actions.FINISH_ANNOTATION) {
            getDeletedFrames('job', jobInstance.id).then((deletedFrames) => {
                const frameIdxs: number[] = [];
                const fetchingFrameAnnotations: Function[] = [];
                for (let idx = jobInstance.startFrame; idx <= jobInstance.stopFrame; idx++) {
                    if (deletedFrames[idx]) {
                        continue;
                    }
                    frameIdxs.push(idx);
                    fetchingFrameAnnotations.push(jobInstance.annotations.get(idx));
                }

                Promise.all(fetchingFrameAnnotations).then((results) => {
                    let unlabelText = '';
                    for (let i = 0; i < frameIdxs.length; i++) {
                        if (results[i].length !== 0) {
                            continue;
                        }
                        const idx = frameIdxs[i];
                        unlabelText += `${idx} `;
                    }
                    Modal.confirm({
                        title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.VALIDATION)}`,
                        content: `${unlabelText} 还未标注。阶段将被切换到：${GetJobStageChinese(JobStage.VALIDATION)}。是否继续？`,
                        okText: '继续',
                        cancelText: '取消',
                        className: 'cvat-modal-content-finish-job',
                        onOk: () => {
                            checkUnsavedChanges(params);
                        },
                    });
                }).catch((error) => {
                    console.log('获取标注失败：', error);
                });
            }).catch((error) => {
                console.log('获取已删除帧失败：', error);
            });
        } else if (params.key === Actions.FINISH_VALIDATION) {
            checkCurrentUserIsAnnotator(user, jobInstance)
                .then((isWorker) => {
                    if (isWorker) {
                        alert('标注人员无权修改审核状态作业的状态');
                    } else {
                        Modal.confirm({
                            title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.INSPECTION)}`,
                            content: `阶段将被切换到：${GetJobStageChinese(JobStage.INSPECTION)}。是否继续？`,
                            okText: '继续',
                            cancelText: '取消',
                            className: 'cvat-modal-content-finish-job',
                            onOk: () => {
                                checkUnsavedChanges(params);
                            },
                        });
                    }
                });
        } else if (params.key === Actions.REVERT_VALIDATION) {
            checkCurrentUserIsAnnotator(user, jobInstance)
                .then((isWorker) => {
                    if (isWorker) {
                        alert('标注人员无权修改审核状态作业的状态');
                    } else {
                        Modal.confirm({
                            title: `您是否想将当前作业的阶段退回到 ${GetJobStageChinese(JobStage.ANNOTATION)}`,
                            content: `阶段将被切换到：${GetJobStageChinese(JobStage.ANNOTATION)}。是否继续？`,
                            okText: '继续',
                            cancelText: '取消',
                            className: 'cvat-modal-content-finish-job',
                            onOk: () => {
                                checkUnsavedChanges(params);
                            },
                        });
                    }
                });
        } else if (params.key === Actions.FINISH_INSPECTION) {
            Modal.confirm({
                title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.QUALITYCTL)}`,
                content: `阶段将被切换到：${GetJobStageChinese(JobStage.QUALITYCTL)}。是否继续？`,
                okText: '继续',
                cancelText: '取消',
                className: 'cvat-modal-content-finish-job',
                onOk: () => {
                    checkUnsavedChanges(params);
                },
            });
        } else if (params.key === Actions.REVERT_INSPECTION) {
            Modal.confirm({
                title: `您是否想将当前作业的阶段退回到 ${GetJobStageChinese(JobStage.VALIDATION)}`,
                content: `阶段将被切换到：${GetJobStageChinese(JobStage.VALIDATION)}。是否继续？`,
                okText: '继续',
                cancelText: '取消',
                className: 'cvat-modal-content-finish-job',
                onOk: () => {
                    checkUnsavedChanges(params);
                },
            });
        } else if (params.key === Actions.FINISH_QUALITYCTL) {
            Modal.confirm({
                title: `您是否想将当前作业的阶段切换到 ${GetJobStageChinese(JobStage.ACCEPTANCE)}`,
                content: `阶段将被切换到：${GetJobStageChinese(JobStage.ACCEPTANCE)}。是否继续？`,
                okText: '继续',
                cancelText: '取消',
                className: 'cvat-modal-content-finish-job',
                onOk: () => {
                    checkUnsavedChanges(params);
                },
            });
        } else if (params.key === Actions.REVERT_QUALITYCTL) {
            Modal.confirm({
                title: `您是否想将当前作业的阶段退回到 ${GetJobStageChinese(JobStage.INSPECTION)}`,
                content: `阶段将被切换到：${GetJobStageChinese(JobStage.INSPECTION)}。是否继续？`,
                okText: '继续',
                cancelText: '取消',
                className: 'cvat-modal-content-finish-job',
                onOk: () => {
                    checkUnsavedChanges(params);
                },
            });
        } else if (params.key === Actions.RENEW_JOB) {
            Modal.confirm({
                title: '您是否想重置当前作业？',
                content: `阶段将被重置为 ${GetJobStageChinese(JobStage.ANNOTATION)}。` +
                    `状态将被重置为 ${GetJobStateChinese(JobState.IN_PROGRESS)}。是否继续？`,
                okText: '继续',
                cancelText: '取消',
                className: 'cvat-modal-content-renew-job',
                onOk: () => {
                    onClickMenu(params);
                },
            });
        } else {
            onClickMenu(params);
        }
    }

    return (
        <Menu
            onClick={(params: MenuInfo) => onClickMenuWrapper(params)}
            className='cvat-annotation-menu'
            selectable={false}
        >
            {user?.isSuperuser && (
                <>
                    <Menu.Item key={Actions.OPEN_TASK}>
                        <a
                            href={`/tasks/${taskID}`}
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                history.push(`/tasks/${taskID}`);
                                return false;
                            }}
                        >
                            打开任务
                        </a>
                    </Menu.Item>
                    {user?.isStaff && (
                        <>
                            <Menu.Item key={Actions.LOAD_JOB_ANNO}>上传标注</Menu.Item>
                            <Menu.Item key={Actions.EXPORT_JOB_DATASET}>导出作业</Menu.Item>
                        </>
                    )}
                </>
            )}

            <Menu.Item key={Actions.REMOVE_ANNO} disabled={jobInstance.locked}>移除标注</Menu.Item>

            {[JobStage.ANNOTATION].includes(jobStage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(jobState) ?
                <Menu.Item key={Actions.FINISH_ANNOTATION} disabled={jobInstance.locked}>完成标注</Menu.Item> : null}

            {[JobStage.VALIDATION].includes(jobStage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(jobState) ?
                <Menu.Item key={Actions.FINISH_VALIDATION} disabled={jobInstance.locked}>完成审核</Menu.Item> : null}
            {[JobStage.VALIDATION].includes(jobStage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(jobState) ?
                <Menu.Item key={Actions.REVERT_VALIDATION} disabled={jobInstance.locked}>退回标注</Menu.Item> : null}

            {[JobStage.INSPECTION].includes(jobStage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(jobState) ?
                hasPermissionForJobAfterInspectionStage(user) &&
                <Menu.Item key={Actions.FINISH_INSPECTION} disabled={jobInstance.locked}>完成质检</Menu.Item> : null}

            {[JobStage.QUALITYCTL].includes(jobStage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(jobState) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.FINISH_QUALITYCTL} disabled={jobInstance.locked}>完成质控</Menu.Item> : null}
            {[JobStage.QUALITYCTL].includes(jobStage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(jobState) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.REVERT_INSPECTION} disabled={jobInstance.locked}>退回审核</Menu.Item> : null}
            {[JobStage.QUALITYCTL].includes(jobStage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(jobState) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.REVERT_QUALITYCTL} disabled={jobInstance.locked}>退回质检</Menu.Item> : null}

            {[JobStage.ACCEPTANCE].includes(jobStage) &&
                [JobState.NEW, JobState.IN_PROGRESS, JobState.REJECTED].includes(jobState) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.FINISH_ACCEPTANCE} disabled={jobInstance.locked}>完成验收</Menu.Item> : null}

            {[JobStage.ACCEPTANCE].includes(jobStage) && [JobState.COMPLETED].includes(jobState) ?
                hasPermissionForJobAfterQualityCtlStage(user) &&
                <Menu.Item key={Actions.RENEW_JOB} disabled={jobInstance.locked}>再次标注</Menu.Item> : null}
        </Menu>
    );
}

export default withRouter(AnnotationMenuComponent);
